import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus, Minus, Trash, ShoppingCart, Tag } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cartKey = (slug) => `korp-cart-${slug}`;

export default function PublicCart() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey(slug)) || "{}"); } catch { return {}; }
  });
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", provider: "paypal", shipping: {} });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { axios.get(`${API}/public/${slug}`).then(r => setData(r.data)).catch(() => {}); }, [slug]);
  useEffect(() => { try { localStorage.setItem(cartKey(slug), JSON.stringify(cart)); } catch (err) { /* quota */ } }, [cart, slug]);

  if (!data) return <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center font-mono-k">Loading…</div>;

  const products = data?.products || [];
  const theme = data.profile?.theme_config || {};
  const accent = theme.accent_color || "#FACC15";
  const displayFont = theme.font_display || "Clash Display";
  const bundleEnabled = data.profile?.bundle_discount_enabled;

  const cartLines = Object.entries(cart)
    .map(([pid, qty]) => {
      const p = products.find(x => x.id === pid);
      return p ? { ...p, qty, line_total: p.price * qty } : null;
    })
    .filter(Boolean);

  const cartCount = cartLines.reduce((s, l) => s + l.qty, 0);
  const distinctCount = cartLines.length;
  const subtotal = cartLines.reduce((s, l) => s + l.line_total, 0);
  const discountPct = bundleEnabled ? (distinctCount >= 4 ? 15 : distinctCount === 3 ? 10 : distinctCount === 2 ? 5 : 0) : 0;
  const discountAmount = subtotal * (discountPct / 100);
  const total = subtotal - discountAmount;
  const needsShipping = cartLines.some(l => l.requires_shipping);

  const setQty = (pid, qty) => setCart(prev => {
    const next = { ...prev };
    if (qty <= 0) delete next[pid]; else next[pid] = Math.min(99, qty);
    return next;
  });
  const clearCart = () => setCart({});

  const checkout = async (e) => {
    e.preventDefault();
    if (cartLines.length === 0) return toast.error("Cart is empty");
    if (needsShipping) {
      const s = form.shipping || {};
      for (const k of ["full_name", "line1", "city", "postal_code", "country"]) {
        if (!s[k]?.trim()) return toast.error("Please complete the shipping address");
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        items: cartLines.map(l => ({ product_id: l.id, qty: l.qty })),
        buyer_name: form.buyer_name.trim(),
        buyer_email: form.buyer_email.trim(),
        provider: form.provider,
      };
      if (needsShipping) payload.shipping_address = form.shipping;
      const { data: order } = await axios.post(`${API}/public/${slug}/checkout-cart`, payload);
      clearCart();
      nav(`/shop-checkout/${order.order_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white noise" style={{ "--accent": accent }} data-testid="public-cart">
      <header className="border-b border-white/10 sticky top-0 bg-[#0A0D14]/95 backdrop-blur-md z-20">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <a href={`/u/${slug}`} className="flex items-center gap-2" data-testid="cart-back">
            <BrandLogo height={36} />
          </a>
          <a href={`/u/${slug}`} className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA] hover:text-white" data-testid="cart-continue-shopping">← CONTINUE SHOPPING</a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="text-xs font-mono-k tracking-widest-k mb-2" style={{ color: accent }}>— CART · {cartCount} ITEM{cartCount === 1 ? "" : "S"}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl" style={{ fontFamily: displayFont }}>Review your order.</h1>
        </div>

        {cartLines.length === 0 ? (
          <div className="border border-white/10 p-12 text-center">
            <ShoppingCart size={48} className="mx-auto mb-4 opacity-40" />
            <div className="font-mono-k text-[#A1A1AA] mb-4">Your cart is empty.</div>
            <a href={`/u/${slug}`} className="k-btn primary inline-flex" data-testid="cart-browse">← BROWSE PRODUCTS</a>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Line items */}
            <div className="lg:col-span-2 space-y-3">
              {cartLines.map(l => (
                <div key={l.id} className="border border-white/10 bg-[#111318] p-4 flex items-start gap-4" data-testid={`cart-line-${l.id}`}>
                  {l.image ? (
                    <img src={l.image} alt="" className="w-24 h-24 object-cover border border-white/10 shrink-0" />
                  ) : (
                    <div className="w-24 h-24 bg-[#0A0D14] border border-white/10 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg truncate">{l.name}</div>
                    {l.description && <div className="text-xs font-mono-k text-[#A1A1AA] mt-1 line-clamp-2">{l.description}</div>}
                    {l.requires_shipping && <div className="text-[10px] font-mono-k tracking-widest-k mt-2" style={{ color: accent }}>· SHIPPING REQUIRED</div>}
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => setQty(l.id, l.qty - 1)} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:bg-white/5" data-testid={`cart-dec-${l.id}`}><Minus size={12} /></button>
                      <div className="font-mono-k text-sm w-8 text-center" data-testid={`cart-qty-${l.id}`}>{l.qty}</div>
                      <button onClick={() => setQty(l.id, l.qty + 1)} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:bg-white/5" data-testid={`cart-inc-${l.id}`}><Plus size={12} /></button>
                      <button onClick={() => setQty(l.id, 0)} className="ml-3 text-[#EF4444] hover:text-red-400" data-testid={`cart-remove-${l.id}`}><Trash size={16} /></button>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-2xl" style={{ color: accent }}>${l.line_total.toFixed(2)}</div>
                    <div className="text-[10px] font-mono-k text-[#A1A1AA]">${l.price.toFixed(2)} each</div>
                  </div>
                </div>
              ))}
              <button onClick={clearCart} className="k-btn text-xs" data-testid="cart-page-clear"><Trash size={12} /> EMPTY CART</button>
            </div>

            {/* Order summary + shipping + checkout */}
            <div>
              <form onSubmit={checkout} className="border border-white/10 bg-[#111318] p-5 space-y-3 sticky top-24">
                <div className="text-xs font-mono-k tracking-widest-k" style={{ color: accent }}>— ORDER SUMMARY</div>
                <div className="flex items-center justify-between text-sm font-mono-k">
                  <span className="text-[#A1A1AA]">Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex items-center justify-between text-sm font-mono-k" style={{ color: accent }}>
                    <span><Tag size={12} className="inline" /> Bundle {discountPct}%</span>
                    <span>−${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                  <span className="text-xs font-mono-k tracking-widest-k text-[#A1A1AA]">TOTAL</span>
                  <span className="font-display text-3xl" style={{ color: accent }}>${total.toFixed(2)}</span>
                </div>

                <div className="border-t border-white/10 pt-3 space-y-2">
                  <input required placeholder="Your name" value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} className="k-input w-full" data-testid="cart-page-name" />
                  <input required type="email" placeholder="Your email (receipt goes here)" value={form.buyer_email} onChange={e => setForm({ ...form, buyer_email: e.target.value })} className="k-input w-full" data-testid="cart-page-email" />
                </div>

                {needsShipping && (
                  <div className="border-t border-white/10 pt-3 space-y-2" data-testid="cart-page-shipping">
                    <div className="text-xs font-mono-k tracking-widest-k" style={{ color: accent }}>— SHIPPING ADDRESS</div>
                    <input required placeholder="Recipient name" value={form.shipping.full_name || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, full_name: e.target.value } })} className="k-input w-full" data-testid="page-ship-name" />
                    <input required placeholder="Street address" value={form.shipping.line1 || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, line1: e.target.value } })} className="k-input w-full" data-testid="page-ship-line1" />
                    <input placeholder="Apt / Suite (optional)" value={form.shipping.line2 || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, line2: e.target.value } })} className="k-input w-full" data-testid="page-ship-line2" />
                    <div className="grid grid-cols-2 gap-2">
                      <input required placeholder="City" value={form.shipping.city || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, city: e.target.value } })} className="k-input" data-testid="page-ship-city" />
                      <input placeholder="State / Region" value={form.shipping.region || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, region: e.target.value } })} className="k-input" data-testid="page-ship-region" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input required placeholder="ZIP / Postal" value={form.shipping.postal_code || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, postal_code: e.target.value } })} className="k-input" data-testid="page-ship-postal" />
                      <input required placeholder="Country" value={form.shipping.country || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, country: e.target.value } })} className="k-input" data-testid="page-ship-country" />
                    </div>
                    <input placeholder="Phone (optional)" value={form.shipping.phone || ""} onChange={e => setForm({ ...form, shipping: { ...form.shipping, phone: e.target.value } })} className="k-input w-full" data-testid="page-ship-phone" />
                  </div>
                )}

                <div className="border-t border-white/10 pt-3">
                  <div className="text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA] mb-2">PAY WITH</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setForm({ ...form, provider: "paypal" })} className={`k-btn text-xs justify-center ${form.provider === "paypal" ? "primary" : ""}`} data-testid="cart-page-paypal">PAYPAL / CARD</button>
                    <button type="button" onClick={() => setForm({ ...form, provider: "nowpayments" })} className={`k-btn text-xs justify-center ${form.provider === "nowpayments" ? "primary" : ""}`} data-testid="cart-page-crypto">USDT / CRYPTO</button>
                  </div>
                </div>

                <button disabled={submitting} className="w-full flex items-center justify-center gap-2 py-4 font-mono-k text-sm tracking-widest-k transition-all hover:opacity-90 disabled:opacity-60" style={{ background: accent, color: "#0A0D14" }} data-testid="cart-page-checkout">
                  {submitting ? "STARTING…" : `CHECKOUT · $${total.toFixed(2)}`} <ArrowRight size={16} />
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
